import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import type { TextFieldOnChangeArguments, TextFieldPropSize } from 'components/FieldNumberFormat';
import { FieldNumberFormat } from 'components/FieldNumberFormat';
import { RequestStatus } from 'features/request/requestSlice';
import type { ServiceDataResultOfInt64__, ServiceIdResultOfInt64, ServiceResult } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import type { Direction, TooltipPropStatus } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { debounce } from 'debounce';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TooltipWrapper } from '../TooltipWrapper';

interface IEditableTableCell {
    rowId?: string | number;
    fieldValue: string;
    decimalScale?: number;
    isDynamicDecimalScale?: boolean;
    disabled?: boolean;
    reloadHandler: (reloadRowsIds?: number[] | null) => void;
    updateRequest: (value: string) => void;
    updateStatus: RequestStatus;
    requestResultObj: Partial<ServiceResult> | Partial<ServiceIdResultOfInt64> | Partial<ServiceDataResultOfInt64__>;
    requestErrors: AnyObject[];
    inputStyles?: CSSProperties;
    tooltipStatus?: TooltipPropStatus;
    toolTipValue?: string | null;
    size?: TextFieldPropSize;
    errorModalTitle?: string;
    tooltipDirection?: Direction;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    onBlur?: React.FocusEventHandler<HTMLElement>;
    inputWrapperClassName?: string;
    isUpdateOnChange?: boolean;
    [x: string]: any;
}

const numberInputStyle = { minWidth: 'unset' };

export const EditableTableCell = (prop: IEditableTableCell) => {
    const ref = useRef<HTMLDivElement>(null);

    const {
        decimalScale,
        isDynamicDecimalScale,
        fieldValue,
        disabled,
        reloadHandler,
        updateRequest,
        updateStatus,
        requestResultObj,
        requestErrors,
        inputStyles,
        tooltipStatus,
        toolTipValue,
        size = 'xs',
        errorModalTitle,
        tooltipDirection = 'rightCenter',
        rowId,
        onFocus,
        onBlur,
        inputWrapperClassName,
        isUpdateOnChange = true,
        ...props
    } = prop;

    const [currentValue, setCurrentValue] = useState(fieldValue);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);

    useEffect(() => {
        if (isDirty && updateStatus === RequestStatus.SUCCESS && requestResultObj?.result) {
            reloadHandler((requestResultObj as ServiceDataResultOfInt64__)?.data || (rowId && [Number(rowId)]) || []);
            setIsDirty(false);
        }
        if (
            isDirty &&
            (updateStatus === RequestStatus.ERROR ||
                (updateStatus === RequestStatus.SUCCESS && !requestResultObj?.result)) &&
            currentValue !== fieldValue
        ) {
            setCurrentValue(fieldValue);
        }
    }, [
        currentValue,
        fieldValue,
        isDirty,
        reloadHandler,
        requestResultObj,
        requestResultObj?.result,
        updateStatus,
        rowId,
    ]);

    useEffect(() => {
        const inputElement = ref.current?.querySelector('input');

        if (inputElement !== document.activeElement) {
            setCurrentValue(fieldValue);
        }
    }, [fieldValue]);

    const handleOnChange = useCallback(
        ({ value }: TextFieldOnChangeArguments) => {
            if (value === '-') {
                return;
            }

            const newValue = value ?? '';

            setCurrentValue(newValue);

            if (isUpdateOnChange) {
                updateRequest(newValue);
            }

            setIsDirty(true);
        },
        [isUpdateOnChange, updateRequest],
    );

    const debouncedOnChange = useMemo(() => debounce(handleOnChange, 500), [handleOnChange]);

    const onMouseEnter = useCallback(() => {
        setIsTooltipVisible(true);
    }, []);

    const onMouseLeave = useCallback(() => {
        setIsTooltipVisible(false);
    }, []);

    const handleCloseModalError = useCallback(() => {
        reloadHandler();
        setIsDirty(false);
    }, [reloadHandler]);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(requestResultObj?.validation);
    }, [requestResultObj?.validation]);

    return (
        <>
            <TooltipWrapper
                direction={tooltipDirection}
                message={toolTipValue}
                isHidden={!toolTipValue || !isTooltipVisible}
                style={{ width: '100%', height: '100%' }}
                toolTipStyle={{ color: 'white' }}
                status={tooltipStatus}
                wrapperRef={ref}
            >
                <FieldNumberFormat
                    decimalScale={decimalScale}
                    value={currentValue}
                    size={size}
                    style={{ width: '100%', ...inputStyles }}
                    onChange={debouncedOnChange}
                    disabled={disabled}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    inputStyle={numberInputStyle}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={inputWrapperClassName}
                    {...props}
                />
            </TooltipWrapper>
            {errorModalTitle && isDirty && (
                <ErrorHandlingModal
                    titleText={errorModalTitle}
                    isSubmitted={isDirty}
                    statusRequest={updateStatus}
                    dataRequest={requestResultObj}
                    errorsRequest={requestErrors}
                    handleCloseModalError={handleCloseModalError}
                    handleCreateReport={handleCreateReport}
                />
            )}
        </>
    );
};

EditableTableCell.displayName = 'EditableTableCell';
